import React, { useState } from "react";
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Button,
  CircularProgress,
  useMediaQuery,
} from "@mui/material";
import ReactMarkdown from "react-markdown";
import { X, Eye, Download } from "lucide-react";
import {
  formatCaseTitle,
  formatCourtName,
  formatRefLabel,
} from "../utils/HelperFunction/textFormatting";

const CASE_CONTENT_STYLES = `
  .MsoNormal { font-family: 'Roboto', Helvetica, Arial, sans-serif !important; line-height: 1.4; font-size: 13px; }
  .MsoNormal b { font-size: 13px; color: #303f9f; }
  p { margin-bottom: 12px; color: #334155; }
  a { display: none; }
`;

const STATUTE_CONTENT_STYLES = `
  .Section1 * { font-family: 'Roboto', Helvetica, Arial, sans-serif !important; }
  .Section1 b { color: #303f9f; }
`;

function ReferenceRow({ item, onViewCase, onViewStatute }) {
  const [expanded, setExpanded] = useState(false);
  // Each item carries its own `type` ("statute" vs everything else treated
  // as a case) — the same convention Legal Chat's references use — so a
  // single mixed list (e.g. "Related Law") routes each row correctly.
  const isStatute = item.type === "statute";
  const title = formatCaseTitle(item.title) || (item.label ? formatRefLabel(item.label) : "") || "Untitled";
  const subtitleParts = [];
  if (!isStatute && item.court) subtitleParts.push(formatCourtName(item.court));
  if (item.label) subtitleParts.push(formatRefLabel(item.label));

  const wrapperStyles = isStatute ? STATUTE_CONTENT_STYLES : CASE_CONTENT_STYLES;
  const wrapperClass = isStatute ? "Section1" : "MsoNormal";

  // Cases/statutes are looked up live via their respective search index
  // (same as Legal Chat's reference preview) rather than rendered from
  // whatever inline content the card happened to include.
  const canSearch = isStatute ? typeof onViewStatute === "function" : typeof onViewCase === "function";
  const canExpandInline = !canSearch && !!item.html_content;
  const showEyeButton = canSearch || canExpandInline;

  const handleRowClick = () => {
    if (canSearch) {
      isStatute ? onViewStatute(item) : onViewCase(item);
    } else if (canExpandInline) {
      setExpanded((prev) => !prev);
    }
  };

  return (
    <Box
      onClick={showEyeButton ? handleRowClick : undefined}
      sx={{
        border: "1px solid #e5e7eb",
        borderRadius: "10px",
        padding: "12px 14px",
        marginBottom: "10px",
        background: "#ffffff",
        cursor: showEyeButton ? "pointer" : "default",
        transition: "border-color 0.15s, background 0.15s",
        "&:hover": showEyeButton ? { borderColor: "#93c5fd", background: "#f8fafc" } : undefined,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "#1e3a8a" }}>{title}</Typography>
          {subtitleParts.length > 0 && (
            <Typography sx={{ fontSize: "12px", color: "#6b7280", mt: 0.25 }}>
              {subtitleParts.map((part, index) => (
                <React.Fragment key={index}>
                  {index > 0 && " · "}
                  {part}
                </React.Fragment>
              ))}
            </Typography>
          )}
        </Box>
        {showEyeButton && (
          <IconButton size="small" sx={{ pointerEvents: "none" }}>
            <Eye size={16} color="#2196f3" />
          </IconButton>
        )}
      </Box>

      {expanded && canExpandInline && (
        <Box
          className={wrapperClass}
          sx={{
            marginTop: "10px",
            paddingTop: "10px",
            borderTop: "1px solid #f1f5f9",
            wordBreak: "break-word",
            fontSize: "13px",
            lineHeight: 1.5,
          }}
          dangerouslySetInnerHTML={{ __html: `<style>${wrapperStyles}</style>${item.html_content}` }}
        />
      )}
    </Box>
  );
}

const CardContentPanelDialog = ({
  open,
  onClose,
  title,
  kind,
  items,
  text,
  onDownload,
  downloading,
  onViewCase,
  onViewStatute,
}) => {
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("sm"));
  const isListView = kind !== "text" && Array.isArray(items) && items.length > 0;

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: isMobile ? "100%" : 460,
          maxWidth: "100%",
          display: "flex",
          flexDirection: "column",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "16px 20px",
          borderBottom: "1px solid #f1f5f9",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: "16px", fontWeight: 700, color: "#111827" }}>{title}</Typography>
        <IconButton size="small" onClick={onClose}>
          <X size={18} />
        </IconButton>
      </Box>

      <Box sx={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "16px 20px" }}>
        {isListView ? (
          items.map((item, index) => (
            <ReferenceRow
              key={item.id || index}
              item={item}
              onViewCase={onViewCase}
              onViewStatute={onViewStatute}
            />
          ))
        ) : (
          <Typography
            component="div"
            sx={{
              fontSize: "14px",
              lineHeight: 1.6,
              color: "#374151",
              "& p": { margin: "0 0 10px" },
            }}
          >
            <ReactMarkdown>{text || "No content available."}</ReactMarkdown>
          </Typography>
        )}
      </Box>

      {!isListView && onDownload && (
        <Box sx={{ padding: "14px 20px", borderTop: "1px solid #f1f5f9", flexShrink: 0 }}>
          <Button
            fullWidth
            variant="contained"
            onClick={onDownload}
            disabled={downloading}
            startIcon={downloading ? <CircularProgress size={16} sx={{ color: "#fff" }} /> : <Download size={16} />}
            sx={{
              backgroundColor: "#3C50B1",
              textTransform: "none",
              borderRadius: "8px",
              "&:hover": { backgroundColor: "#2f3f8e" },
            }}
          >
            {downloading ? "Generating…" : "Download as Word"}
          </Button>
        </Box>
      )}
    </Drawer>
  );
};

export default CardContentPanelDialog;
